import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { useExperience } from '../../contexts/ExperienceContext';
import { deeperSections } from '../../data/journeys';
import { needById } from '../../data/needs';
import { Eyebrow, Stage } from '../ui/Stage';
import type { NeedId } from '../../types/filtek';
const ease = [0.16, 1, 0.3, 1] as const;
const ctaToSection: Record<string, string> = {
  technique: 'techniques',
  product: 'products',
  evidence: 'evidence',
  case: 'cases',
  contact: 'products'
};
const keywords: Record<NeedId, string[]> = {
  aesthetic: ['Supreme', 'Diastema', 'stratification', 'gloss'],
  everyday: ['Easy Match', 'Shade', 'shade', 'Finishing', 'five minutes'],
  posterior: ['Bulk', 'Class II', '5 mm', 'Posterior', 'posterior', 'quadrant'],
  handling: ['Warmer', 'warmed', 'Class V', 'Viscosity', 'adaptation']
};

/** The repository — available, deliberately quiet, ordered by what you told us. */
export function DeeperStage() {
  const {
    signals,
    push,
    reset,
    direction,
    primaryNeed
  } = useExperience();
  const [open, setOpen] = useState<string | undefined>();
  const need = needById(primaryNeed);
  const sections = useMemo(() => {
    const preferred = signals.cta ? ctaToSection[signals.cta] : undefined;
    if (!preferred) return deeperSections;
    return [...deeperSections].sort((a, b) => a.id === preferred ? -1 : b.id === preferred ? 1 : 0);
  }, [signals.cta]);
  const toggle = (id: string, label: string) => {
    setOpen(open === id ? undefined : id);
    push('contentExplored', label);
  };
  return <Stage direction={direction} align="start" className="min-h-screen py-28 sm:py-32" labelledBy="deeper-title" data-id="element-194" data-mp="1s7pb4jezq2be">
      <div className="max-w-xl" data-id="element-195" data-mp="1i8q7xgczuyvs">
        <Eyebrow accent={need.accent} data-id="element-196" data-mp="1i7yfgeep1h9q">Everything else</Eyebrow>
        <h2 id="deeper-title" className="mt-6 text-2xl font-light tracking-[-0.02em] text-enamel" data-id="element-197" data-mp="1da6mdh2bbp3y">
          Go deeper, if you want to
        </h2>
        <p className="mt-5 text-sm font-light leading-relaxed text-mute" data-id="element-198" data-mp="13kpwusj229nq">
          The full library sits behind the story. Items relevant to{' '}
          <span className="text-enamel" data-id="element-199" data-mp="ulw3db10tzee">{need.label.toLowerCase()}</span> are marked.
        </p>
      </div>

      <ul className="mt-14 border-t border-enamel/10" data-id="element-200" data-mp="ymods7lmqac9">
        {sections.map(section => {
        const isOpen = open === section.id;
        return <li key={section.id} className="border-b border-enamel/10" data-id="element-201" data-mp="1mevgdohy7dk4">
              <button type="button" onClick={() => toggle(section.id, section.label)} aria-expanded={isOpen} className="group flex w-full items-center justify-between gap-6 py-5 text-left" data-id="element-202" data-mp="1raka3b7ae08i">
                <span className="flex items-baseline gap-5" data-id="element-203" data-mp="25b4qy1tn5lzd">
                  <span className="text-xl font-light tracking-[-0.02em] text-enamel" data-id="element-204" data-mp="259tny480si9b">
                    {section.label}
                  </span>
                  <span className="label-mono text-[9px] text-mute/60" data-id="element-205" data-mp="3us0em61f8hvb">
                    {section.items.length}
                  </span>
                </span>
                <motion.span animate={{
              rotate: isOpen ? 180 : 0
            }} transition={{
              duration: 0.4
            }} data-id="element-206" data-mp="8d7h95ptjvsn">
                  <ChevronDownIcon className="h-4 w-4 text-mute" aria-hidden="true" data-id="element-207" data-mp="3e49vemd9dhi" />
                </motion.span>
              </button>

              <AnimatePresence initial={false} data-id="element-208" data-mp="3buz4k6t0frki">
                {isOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.5,
              ease
            }} className="overflow-hidden" data-id="element-209" data-mp="36vcpdrtmg192">
                    <ul className="space-y-3 pb-8" data-id="element-210" data-mp="110jtalycsl7p">
                      {section.items.map(item => {
                  const relevant = keywords[need.id].some(word => item.includes(word));
                  return <li key={item} className="flex items-center gap-3" data-id="element-211" data-mp="31texh4pmrzq9">
                            <span className="h-1 w-1 shrink-0 rounded-full" style={{
                      backgroundColor: relevant ? need.accent : 'rgba(244,242,237,0.2)'
                    }} aria-hidden="true" data-id="element-212" data-mp="36qf3xgqe74hw" />
                            <span className={`text-sm font-light ${relevant ? 'text-enamel' : 'text-mute'}`} data-id="element-213" data-mp="3pohkd74vjt43">
                              {item}
                            </span>
                            {relevant && <span className="label-mono text-[8px] text-mute/70" data-id="element-214" data-mp="7cuvll3uxab2">
                                For your focus
                              </span>}
                          </li>;
                })}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </li>;
      })}
      </ul>

      <footer className="mt-24 border-t border-enamel/10 pt-12" data-id="element-215" data-mp="8ea5qewdv2bq">
        <p className="max-w-2xl text-[1.35rem] font-light leading-snug tracking-[-0.02em] text-enamel sm:text-[1.7rem]" data-id="element-216" data-mp="8fv3eddzv1dt">
          Filtek is not one composite trying to solve every situation. It is a family that gives
          clinicians different solutions for different restorative demands —{' '}
          <span className="text-mute" data-id="element-217" data-mp="vcwwybod2t2j">within one coherent system.</span>
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-218" data-mp="rcd1dcjfrxii">
          <button type="button" onClick={reset} className="label-mono text-[10px] text-mute transition-colors hover:text-enamel" data-id="element-219" data-mp="1nval1jjwb0of">
            Start the conversation again
          </button>
          <span className="label-mono text-[10px] text-mute/50" data-id="element-220" data-mp="3dcy1kqtw1g6y">
            Solventum · Filtek Family · For dental professionals
          </span>
        </div>
      </footer>
    </Stage>;
}